import React from "react";
import { Controller } from "react-hook-form";
import Flatpickr from "react-flatpickr";

export const DatePickerField = ({
  control,
  name,
  label = "Date",
  placeholder = "Select Date",
  rules = {},
  className = "",
  disabled = false,
  options,
  disabledMarginBottom,
  ...props
}) => {
  const isRequired = !!rules?.required;
  const customIconStyle = { top: "17px", position: "absolute" };

  const inputBg = getComputedStyle(document.documentElement).getPropertyValue("--vz-tertiary-bg").trim();
  const inputColor = getComputedStyle(document.documentElement).getPropertyValue("--vz-body-color").trim();
  const inputBorder = getComputedStyle(document.documentElement).getPropertyValue("--vz-input-border-custom").trim();

  const disabledStyle = {
    backgroundColor: inputBg || "#eff2f7",
    color: inputColor || "#6c757d",
    border: `1px solid ${inputBorder || "#ced4da"}`,
    opacity: 1,
  };

  const formatLocalDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Helper function to check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Adjust maxDate: use maxDate's date + current time + 10 minutes
  const adjustedOptions = React.useMemo(() => {
    if (!options?.maxDate) return options;

    const maxDate = new Date(options.maxDate);
    const now = new Date();
    
    // Create a new date with maxDate's date but current time + 10 minutes
    const adjustedMaxDate = new Date(
      maxDate.getFullYear(),
      maxDate.getMonth(),
      maxDate.getDate(),
      now.getHours(),
      now.getMinutes() + 10,
      now.getSeconds(),
      now.getMilliseconds()
    );

    return {
      ...options,
      maxDate: adjustedMaxDate
    };
  }, [options]);

  return (
    <div className={disabledMarginBottom ? "mb-0" : "mb-3"}>
      <style>
        {`
          .required-asterisk {
            color: red;
            margin-left: 4px;
          }
        `}
      </style>

      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div className="form-icon">
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field, fieldState }) => {
            let fieldValue = null;

            if (field.value) {
              if (Array.isArray(field.value)) {
                fieldValue = field.value.map((v) => (typeof v === "string" ? new Date(v) : v));
              } else if (typeof field.value === "string") {
                fieldValue = new Date(field.value);
              } else {
                fieldValue = field.value;
              }
            }

            return (
              <>
                <Flatpickr
                  id={name}
                  value={fieldValue}
                  disabled={disabled}
                  style={disabled ? disabledStyle : {}}
                  className={`form-control form-control-icon custom-input ${fieldState.error ? "is-invalid" : ""} ${className}`}
                  options={{
                    dateFormat: "d M, Y",
                    allowInput: true,
                    disableMobile: true,
                    ...adjustedOptions,
                  }}
                  placeholder={placeholder}
                  onChange={(selectedDates) => {
                    if (!selectedDates || selectedDates.length === 0) {
                      field.onChange(null);
                      return;
                    }
                    if (selectedDates.length === 1) {
                      const selectedDate = new Date(selectedDates[0]);
                      
                      // Only apply current time if the selected date is today
                      if (isToday(selectedDate)) {
                        const now = new Date();
                        selectedDate.setHours(now.getHours());
                        selectedDate.setMinutes(now.getMinutes());
                        selectedDate.setSeconds(now.getSeconds());
                        selectedDate.setMilliseconds(now.getMilliseconds());
                      }
                      
                      field.onChange(formatLocalDateTime(selectedDate));
                    } else if (selectedDates.length === 2) {
                      const [start, end] = selectedDates;
                      
                      const startDate = new Date(start);
                      const endDate = new Date(end);
                      
                      // Only apply current time if the dates are today
                      if (isToday(startDate)) {
                        const now = new Date();
                        startDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
                      }
                      
                      if (isToday(endDate)) {
                        const now = new Date();
                        endDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
                      }
                      
                      field.onChange([formatLocalDateTime(startDate), formatLocalDateTime(endDate)]);
                    }
                  }}
                  onReady={(selectedDates, dateStr, instance) => {
                    if (fieldValue) {
                      instance.setDate(fieldValue, false);
                    }
                  }}
                  {...props}
                />

                <div style={customIconStyle}>
                  <i className="ri-calendar-event-line text-muted"></i>
                </div>

                {fieldState.error && (
                  <div className="invalid-feedback">{fieldState.error.message}</div>
                )}
              </>
            );
          }}
        />
      </div>
    </div>
  );
};